import React, { useEffect, useMemo, useRef, useState } from "react";
import CommonLayout from "layouts/DashboardLayout";
import {
  Box, InputAdornment, TextField, Typography, Stack, Grid, IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TimeSlotDayAccordion from "./TimeSlotDayAccordion";
import { useDispatch } from "react-redux";
import { deleteTimeSlot, fetchTimeSlotData } from "../../../redux/slices/hyperwolf/settings";
import { useSearchParams } from "react-router-dom";
import SlotLogs from "./SlotLogs";
import CreateTimeSlots from "./CreateTimeSlots";
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from "../../../redux/slices/modalSlice";
import { useSelector } from "react-redux";
import Loader from "common/Loader";
import MDButton from 'components/MDButton';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { CloseRounded } from '@mui/icons-material';
import EditTimeSlots from "./EditTimeSlot";
import { openSnackbar } from "../../../redux/slices/common";
import { useDebounce } from "hooks/useDebounce";
import NoDataFound from "common/component/NoDataFound";
import { hasSectionPermission } from "utilities/common";
import { getPermissionsByMode } from "utilities/common";
import { getData } from "utilities/common";

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export default function TimeSlotManager() {
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const fullRoleTab = useMemo(() => searchParams.get('roleTab'), [searchParams]);
  // timeSlots
  const searchTermRef = useRef('');
  const { timeSlots, isfetchLoading } = useSelector(state => state.hyperwolfSettings);
  const { user } = useSelector(state => state.auth);

  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }

  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Settings", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Settings", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Settings", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Settings", "Delete");

  useEffect(() => {
    dispatch(fetchTimeSlotData())
      .unwrap()
      .then((res) => {
      })
      .catch((err) => {
      });
  }, [fullRoleTab]);

  const searchHandler = (event) => {
    setSearchTerm(event);
    searchTermRef.current = event;
    handleSearch(event);
    // Debounce the search callback
  };

  const handleSearch = useDebounce(() => {
    dispatch(fetchTimeSlotData({ searchTerm: searchTermRef.current }))
      .unwrap()
      .then((res) => {
      })
      .catch((err) => {
      });
  }, 500);

  const ActionDeleteHandler = (slot, dayID) => {
    dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: { slotid: slot?._id, dayID } }, modal: 'DELETE_MODAL' }))
  }

  const deleteHandler = (id) => {
    const payload = {
      updatedBy: user?.name,
    }

    dispatch(setModalLoader(true));
    dispatch(deleteTimeSlot({ id, payload })).unwrap().then((res) => {
      showResponseMessage(res?.data?.message, 'success');
      dispatch(fetchTimeSlotData())
      dispatch(closeCustomModalDialog());
    }).catch((err) => {
      dispatch(setModalLoader(false));
      showResponseMessage(err?.message, 'error');
    });
  };

  const showResponseMessage = (message, errortype) => {
    if (errortype === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const editHandler = (id) => {
    setSearchParams({ roleTab: `editSlotLogs`, dayId: id });
  }

  const sortedTimeSlots = [...(timeSlots?.timeSlots || [])].sort((a, b) => {
    return daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day);
  });

  return (
    <CommonLayout>
      {fullRoleTab === 'editSlotLogs' ?
        <EditTimeSlots /> :
        fullRoleTab === 'createSlot' ?
          <CreateTimeSlots /> :

          fullRoleTab === 'slotLogs' ?
            <SlotLogs /> :
            <Box p={{ xs: 2, sm: 3, lg: 4 }} className="row-cursor time-slot-management">

              <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
                <Grid item xs={12}>
                  <div className='w-100 d-flex align-item-center jc-s-btwn gap-2 page-res-head'>
                    <Typography variant='h4'>Time Slot Management</Typography>
                    <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
                      <TextField className="custom-search-field" placeholder="Search" label="" value={searchTerm} onChange={(e) => searchHandler(e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">
                            <IconButton size='small'>
                              <SearchIcon width='18px' height='18px' />
                            </IconButton>
                          </InputAdornment>,
                          endAdornment: searchTerm?.length > 0 ? <InputAdornment>
                            <IconButton onClick={() => searchHandler('')} size='small'>
                              <CloseRounded />
                            </IconButton>
                          </InputAdornment> : null
                        }}
                      />
                      {
                        canView &&
                        <MDButton
                          onClick={() => { setSearchParams({ roleTab: `slotLogs` }); }}
                          variant="contained"
                          color="warning"
                          sx={{ minWidth: "170px", background: "#2E2E3A !important", boxShadow: "none !important" }}
                        >
                          Activity Log
                        </MDButton>
                      }
                      {
                        canCreate &&
                        <MDButton
                          startIcon={<AddRoundedIcon fontSize='large' />}
                          variant="contained"
                          color="primary"

                          onClick={() => { setSearchParams({ roleTab: `createSlot` }); }}
                          sx={{ minWidth: "170px" }}
                        >
                          Create Time Slot
                        </MDButton>
                      }
                    </Stack>
                  </div>
                </Grid>
              </Grid>

              {canView &&
                isfetchLoading ? <Loader /> :
                  <div className="time-slot-day-acc">
                    {
                      timeSlots?.timeSlots?.length === 0 ?
                        <NoDataFound />
                        :
                        <>
                          {
                            sortedTimeSlots?.map((data) => {
                              return (
                                <TimeSlotDayAccordion
                                  canCreate={canCreate}
                                  canEdit={canEdit}
                                  canDelete={canDelete}
                                  key={data?._id}
                                  day={data?.day}
                                  slots={data?.slots}
                                  onEdit={(slot) => editHandler(data?._id)}
                                  onDelete={(slot) => ActionDeleteHandler(slot, data?._id)}
                                />
                              )
                            })
                          }
                          {/* {
                            (timeSlots?.timeSlots || [])
                              .sort((a, b) => daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day))
                              .map((data) => (
                                <TimeSlotDayAccordion
                                  key={data._id}
                                  day={data.day}
                                  slots={data.slots}
                                  onEdit={() => editHandler(data._id)}
                                  onDelete={(slot) => ActionDeleteHandler(slot, data._id)}
                                />
                              ))
                          } */}
                        </>
                    }
                  </div>
              }
            </Box>
      }
    </CommonLayout>
  );
}
